import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, X, Wrench, ChevronDown, ChevronUp, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Surfaces a non-blocking warning when the admin's org has applications or
 * candidates with an offered CTC above the ₹50 Cr ceiling. Aggregations
 * silently exclude these rows now (see Joined CTC Outlier Cap fix), but the
 * underlying records remain dirty until an admin cleans them up.
 *
 * Expandable: clicking "Show top N" lists the worst offenders with an
 * inline "Set to ₹X.X L?" button per row that calls the existing PATCH
 * endpoints. The full /admin/ctc-cleanup page stays for bulk operations.
 *
 * Admin-only. Dismissible per-day.
 */

const fmtRupees = (n) => {
  if (!n) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
};

const CtcOutlierBanner = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null); // { applications, candidates }
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Local set of ids the admin has already cleaned in this session — used to
  // hide rows immediately without waiting for a refetch, and to detect when
  // the visible list is empty so we can collapse / clear the banner.
  const [cleanedIds, setCleanedIds] = useState(() => new Set());
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const key = `ctc-outlier-banner-${new Date().toDateString()}`;
    return localStorage.getItem(key) === '1';
  });

  const isAdmin = user?.role === 'admin';

  const reload = useCallback(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/admin/ctc-outliers/count`)
      .then((res) => {
        setCount(res.data?.total || 0);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || dismissed) return;
    reload();
  }, [isAdmin, dismissed, reload]);

  // Lazy-load the top-N records the first time the admin expands. Limited to
  // 5 so the banner stays glanceable; the full list lives on /admin/ctc-cleanup.
  useEffect(() => {
    if (!expanded || details || detailsLoading) return;
    setDetailsLoading(true);
    axios
      .get(`${API}/admin/ctc-outliers`, { params: { limit: 5 } })
      .then((res) => setDetails(res.data || { applications: [], candidates: [] }))
      .catch(() => toast.error('Could not load outlier list'))
      .finally(() => setDetailsLoading(false));
  }, [expanded, details, detailsLoading]);

  if (!isAdmin || dismissed || !loaded || count <= 0) return null;

  const handleDismiss = () => {
    const key = `ctc-outlier-banner-${new Date().toDateString()}`;
    try { localStorage.setItem(key, '1'); } catch { /* quota — ignore */ }
    setDismissed(true);
  };

  const applyAppFix = async (app) => {
    if (savingId) return;
    setSavingId(`app-${app.id}`);
    try {
      await axios.patch(`${API}/admin/ctc-outliers/application/${app.id}`, {
        operation: 'divide_100k',
      });
      toast.success(`Fixed ${app.candidate_name || 'application'}`);
      setCleanedIds((prev) => new Set(prev).add(`app-${app.id}`));
      // Optimistically drop the count too so the banner header stays accurate.
      setCount((c) => Math.max(0, c - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply fix');
    } finally {
      setSavingId(null);
    }
  };

  const applyCandFix = async (cand, field) => {
    const key = `cand-${cand.id}-${field}`;
    if (savingId) return;
    setSavingId(key);
    try {
      await axios.patch(
        `${API}/admin/ctc-outliers/candidate/${cand.id}?field=${field}`,
        { operation: 'divide_100k' },
      );
      toast.success(`Fixed ${cand.name}`);
      setCleanedIds((prev) => new Set(prev).add(key));
      setCount((c) => Math.max(0, c - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply fix');
    } finally {
      setSavingId(null);
    }
  };

  const visibleApps = (details?.applications || []).filter(
    (a) => !cleanedIds.has(`app-${a.id}`),
  );
  const visibleCands = (details?.candidates || []).flatMap((c) => {
    const rows = [];
    if (c.suggested_expected_ctc && !cleanedIds.has(`cand-${c.id}-expected_ctc`)) {
      rows.push({ cand: c, field: 'expected_ctc', value: c.expected_ctc, suggested: c.suggested_expected_ctc });
    }
    if (c.suggested_current_ctc && !cleanedIds.has(`cand-${c.id}-current_ctc`)) {
      rows.push({ cand: c, field: 'current_ctc', value: c.current_ctc, suggested: c.suggested_current_ctc });
    }
    return rows;
  });
  const hasRowsToShow = visibleApps.length + visibleCands.length > 0;

  return (
    <div
      className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-900 shadow-sm"
      role="alert"
      data-testid="ctc-outlier-banner"
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="w-8 h-8 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" data-testid="ctc-outlier-count">
            {count} record{count === 1 ? '' : 's'} with an out-of-range offered CTC
          </div>
          <div className="text-[11px] text-amber-800/80 mt-0.5">
            Joined CTC totals silently exclude these for now, but the underlying values
            are still wrong. Fix inline below or open the full cleanup tool.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-amber-300 bg-white/80 hover:bg-white text-amber-900 whitespace-nowrap"
          data-testid="ctc-outlier-banner-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" /> Hide
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" /> Show top 5
            </>
          )}
        </button>
        <Link
          to="/admin/ctc-cleanup"
          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition whitespace-nowrap flex-shrink-0"
          data-testid="ctc-outlier-banner-cta"
        >
          <Wrench className="w-3 h-3" /> Open cleanup tool
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss for today"
          className="text-amber-700/60 hover:text-amber-900 flex-shrink-0"
          data-testid="ctc-outlier-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-amber-200 px-3 py-2 bg-white/40">
          {detailsLoading ? (
            <div className="flex items-center gap-2 text-[12px] text-amber-800/80">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading worst offenders…
            </div>
          ) : !hasRowsToShow ? (
            <div className="text-[12px] text-emerald-800 flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Top 5 cleared. Reload to load the next batch, or open the full tool for the rest.
            </div>
          ) : (
            <div className="space-y-1">
              {visibleApps.map((a) => {
                const rowKey = `app-${a.id}`;
                const isSaving = savingId === rowKey;
                return (
                  <div
                    key={rowKey}
                    className="flex items-center gap-2 text-[12px] py-1"
                    data-testid={`ctc-outlier-row-app-${a.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 truncate">
                        {a.candidate_name || 'Application'}
                        <span className="ml-1 text-slate-500 font-normal">
                          · {a.job_title || '—'}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        offered_ctc = <span className="font-mono text-rose-700">{fmtRupees(a.offered_ctc)}</span>
                      </div>
                    </div>
                    {a.suggested_offered_ctc ? (
                      <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => applyAppFix(a)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`ctc-outlier-app-apply-${a.id}`}
                      >
                        {isSaving ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                        Set to ₹{a.suggested_offered_ctc} L
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-500 px-2 py-1">
                        No safe suggestion — open tool
                      </span>
                    )}
                  </div>
                );
              })}

              {visibleCands.map(({ cand, field, value, suggested }) => {
                const rowKey = `cand-${cand.id}-${field}`;
                const isSaving = savingId === rowKey;
                return (
                  <div
                    key={rowKey}
                    className="flex items-center gap-2 text-[12px] py-1"
                    data-testid={`ctc-outlier-row-cand-${cand.id}-${field}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 truncate">
                        {cand.name || 'Candidate'}
                        <span className="ml-1 text-slate-500 font-normal">
                          · {field === 'expected_ctc' ? 'Expected' : 'Current'}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {field} = <span className="font-mono text-rose-700">{fmtRupees(value)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => applyCandFix(cand, field)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid={`ctc-outlier-cand-apply-${cand.id}-${field}`}
                    >
                      {isSaving ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                      Set to ₹{suggested} L
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CtcOutlierBanner;
